import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Minus } from 'lucide-react';
import { getAvailableFields, getPinnedFields, savePinnedFields, resetPinnedFields } from '../api/pinnedFields';

const PIN_CAP = 15;

/**
 * Pin manager for the current user's pinned fields on a record type.
 * Two columns: left = pinned (in order, with up/down arrows), right = available
 * (searchable). Hard cap of 15 enforced both client- and server-side.
 *
 * Usage modes:
 *   1. Modal (default) — pass `currentPins`, `onClose`, `onSaved`. Rendered
 *      as a fixed overlay. Close + save return control to the parent.
 *   2. Embedded (embedded={true}) — rendered inline with no overlay shell.
 *      Loads `currentPins` itself via getPinnedFields(recordTypeId), so the
 *      caller doesn't have to pre-fetch. onClose is a no-op; onSaved
 *      triggers a reload of the pin state.
 *
 * Props:
 *   recordTypeId: UUID
 *   currentPins:  (modal mode) array of field_definition_ids in current pin order
 *   onClose:      () => void   (modal mode: closes the overlay)
 *   onSaved:      () => void   (modal mode: parent reloads and closes)
 *   embedded:     boolean      (if true, render inline + self-load pins)
 */
export default function PinFieldManager({ recordTypeId, currentPins, onClose, onSaved, embedded = false }) {
  const [available, setAvailable] = useState([]);
  const [pinned, setPinned] = useState(embedded ? [] : (currentPins || []));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, [recordTypeId]);

  const load = async () => {
    try {
      const fields = await getAvailableFields(recordTypeId);
      setAvailable(fields.fields || []);
      if (embedded) {
        // Self-load current pins so the ProfilePanel caller doesn't have to pre-fetch.
        const pinsResp = await getPinnedFields(recordTypeId);
        setPinned((pinsResp.pins || []).map((p) => p.field_definition_id));
      }
    } catch (err) {
      setError('Failed to load fields');
    }
  };

  const fieldMap = {};
  for (const f of available) fieldMap[f.id] = f;

  const handleAdd = (fieldId) => {
    if (pinned.length >= PIN_CAP) {
      setError(`Cannot pin more than ${PIN_CAP} fields`);
      return;
    }
    setError(null);
    if (!pinned.includes(fieldId)) setPinned([...pinned, fieldId]);
  };

  const handleRemove = (fieldId) => {
    setError(null);
    setPinned(pinned.filter(id => id !== fieldId));
  };

  const handleMove = (index, direction) => {
    const next = [...pinned];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPinned(next);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await savePinnedFields(recordTypeId, pinned);
      // Broadcast so any mounted PinnedFieldsSection reloads instantly
      window.dispatchEvent(new CustomEvent('refresh-pinned-fields'));
      if (embedded) {
        // Stay mounted in the ProfilePanel tab; just reload the pin state.
        await load();
      } else if (onSaved) {
        onSaved();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);
    try {
      await resetPinnedFields(recordTypeId);
      window.dispatchEvent(new CustomEvent('refresh-pinned-fields'));
      if (embedded) {
        setPinned([]);
      } else if (onSaved) {
        onSaved();
      }
    } catch (err) {
      setError('Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const filteredAvailable = available.filter(f => {
    if (pinned.includes(f.id)) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (f.label || '').toLowerCase().includes(s) || (f.name || '').toLowerCase().includes(s);
  });

  // Shared UI elements used by both embedded and modal modes.
  const body = (
    <div className="flex gap-3 p-4 overflow-hidden min-h-0" style={{ minHeight: embedded ? 320 : undefined }}>
      {/* Left: pinned */}
      <div className="w-1/2 flex flex-col min-h-0">
        <div className="text-xs text-indigo-400 font-semibold mb-2 uppercase tracking-wide">
          Pinned ({pinned.length}/{PIN_CAP})
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 pr-1">
          {pinned.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No pins — defaults by sort order will be shown.</p>
          ) : (
            pinned.map((fieldId, i) => {
              const f = fieldMap[fieldId];
              if (!f) return null;
              return (
                <div key={fieldId} className="flex items-center gap-2 bg-gray-800/60 px-2 py-1.5 rounded">
                  <span className="text-[10px] text-gray-500 w-5 text-right">{i + 1}</span>
                  <div className="flex flex-col">
                    <button onClick={() => handleMove(i, -1)} className="text-gray-500 hover:text-indigo-400 text-[10px] leading-none">▲</button>
                    <button onClick={() => handleMove(i, 1)} className="text-gray-500 hover:text-indigo-400 text-[10px] leading-none">▼</button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-200 truncate">{f.label || f.name}</div>
                    <div className="text-[10px] text-gray-500">
                      {f.field_type}
                      {f.visibility_rules ? ' · conditional' : ''}
                    </div>
                  </div>
                  <button onClick={() => handleRemove(fieldId)} className="text-red-400 hover:text-red-300">
                    <Minus className="h-4 w-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right: available */}
      <div className="w-1/2 flex flex-col min-h-0 border-l border-gray-800 pl-3">
        <div className="text-xs text-indigo-400 font-semibold mb-2 uppercase tracking-wide">Available</div>
        <div className="relative mb-2">
          <Search className="h-4 w-4 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search fields..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 pr-1">
          {filteredAvailable.length === 0 ? (
            <p className="text-xs text-gray-500 italic px-2">No matching fields.</p>
          ) : filteredAvailable.map((f) => (
            <div key={f.id} className="flex items-center gap-2 bg-gray-800/60 px-2 py-1.5 rounded">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-200 truncate">{f.label || f.name}</div>
                <div className="text-[10px] text-gray-500">
                  {f.field_type}
                  {f.visibility_rules ? ' · conditional' : ''}
                </div>
              </div>
              <button
                onClick={() => handleAdd(f.id)}
                disabled={pinned.length >= PIN_CAP}
                className="text-indigo-400 hover:text-indigo-300 disabled:text-gray-700"
                title={pinned.length >= PIN_CAP ? `Cap of ${PIN_CAP} reached` : 'Pin this field'}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-between items-center p-3 border-t border-gray-800 flex-shrink-0">
      <div className="flex items-center gap-3">
        <button
          onClick={() => handleReset()}
          disabled={saving}
          className="text-xs text-gray-400 hover:text-gray-200"
        >
          Reset to defaults
        </button>
        {error && <span className="text-xs text-red-400">{error}</span>}
      </div>
      <div className="flex gap-2">
        {!embedded && (
          <button
            onClick={() => onClose && onClose()}
            className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
        )}
        <button
          onClick={() => handleSave()}
          disabled={saving}
          className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Pins'}
        </button>
      </div>
    </div>
  );

  // ── Embedded mode: no overlay, simple panel inside parent container ──
  if (embedded) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl flex flex-col" style={{ maxHeight: 460 }}>
        {body}
        {footer}
      </div>
    );
  }

  // ── Modal mode (default): fixed overlay ──
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => onClose && onClose()}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 flex-shrink-0">
          <p className="text-sm font-semibold text-white">📌 Manage Pinned Fields</p>
          <button onClick={() => onClose && onClose()} className="text-gray-500 hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>
        {body}
        {footer}
      </div>
    </div>
  );
}
